import { useState, useEffect, useRef } from 'react';
import { updatePhotoMetadata } from '../api/photoApi';

const MONTH_NAMES = [
  '', 'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function formatEventDate(iso) {
  if (!iso) return '';
  const d = new Date(iso.includes('T') ? iso : iso + 'T00:00:00Z');
  if (isNaN(d.getTime())) return '';
  const y = d.getUTCFullYear();
  const m = MONTH_NAMES[d.getUTCMonth() + 1];
  const day = d.getUTCDate();
  return `${m} ${day}, ${y}`;
}

// ── Sidebar field — click to edit, blur/Enter to save, Escape to cancel ───────

function LbField({ label, field, photo, editingField, draftValue, saving,
  onStart, onCancel, onChange, onCommit, inputType = 'text', multiline = false }) {

  const isEditing = editingField === field;
  const raw = photo[field] ?? '';
  const display = inputType === 'date' && raw ? formatEventDate(raw) : raw;

  function handleKeyDown(e) {
    if (e.key === 'Escape') { onCancel(); return; }
    if (e.key === 'Enter' && !multiline) { e.preventDefault(); onCommit(); }
  }

  function handleBlur() {
    if (!saving) onCommit();
  }

  function handleClick() {
    const initVal = inputType === 'date' ? raw.split('T')[0] : raw;
    onStart(field, initVal);
  }

  return (
    <div className="fv-lb-field">
      <span className="fv-lb-field-label">{label}</span>
      {isEditing ? (
        multiline ? (
          <textarea
            className="fv-lb-field-input"
            value={draftValue}
            onChange={e => onChange(e.target.value)}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            autoFocus
            rows={3}
          />
        ) : (
          <input
            className="fv-lb-field-input"
            type={inputType}
            value={draftValue}
            onChange={e => onChange(e.target.value)}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            autoFocus
          />
        )
      ) : (
        <p
          className={`fv-lb-field-value${!raw ? ' fv-lb-field-empty' : ''}`}
          onClick={handleClick}
        >
          {display || '—'}
        </p>
      )}
      {isEditing && saving && <span className="fv-lb-saving">Saving…</span>}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function PhotoLightbox({ photo: initialPhoto, prevPhoto, nextPhoto,
  onClose, onPrev, onNext, getToken }) {

  const [photo,        setPhoto]        = useState(initialPhoto);
  const [editingField, setEditingField] = useState(null);
  const [draftValue,   setDraftValue]   = useState('');
  const [saving,       setSaving]       = useState(false);
  const [saveError,    setSaveError]    = useState(null);
  const [sidebarOpen,  setSidebarOpen]  = useState(true);
  const touchStartX = useRef(null);

  // Sync photo when navigating
  useEffect(() => {
    setPhoto(initialPhoto);
    setEditingField(null);
    setDraftValue('');
    setSaveError(null);
  }, [initialPhoto.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Body scroll lock
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  // Keyboard: arrows + Escape
  useEffect(() => {
    function onKey(e) {
      if (e.key === 'Escape')     { onClose(); }
      if (e.key === 'ArrowLeft'  && prevPhoto) { onPrev?.(); }
      if (e.key === 'ArrowRight' && nextPhoto) { onNext?.(); }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, onPrev, onNext, prevPhoto, nextPhoto]);

  // Touch swipe
  function onTouchStart(e) { touchStartX.current = e.touches[0].clientX; }
  function onTouchEnd(e) {
    if (touchStartX.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    touchStartX.current = null;
    if (delta < -50 && nextPhoto) onNext?.();
    if (delta >  50 && prevPhoto) onPrev?.();
  }

  function startEdit(field, value) {
    setEditingField(field);
    setDraftValue(value ?? '');
    setSaveError(null);
  }

  function cancelEdit() {
    setEditingField(null);
    setDraftValue('');
    setSaveError(null);
  }

  async function commitEdit() {
    if (!editingField) return;
    const newValue = draftValue.trim();
    const existing = (photo[editingField] ?? '').split('T')[0] || (photo[editingField] ?? '');
    if (newValue === existing || newValue === (photo[editingField] ?? '')) {
      cancelEdit();
      return;
    }
    setSaving(true);
    try {
      const token = await getToken();
      await updatePhotoMetadata(token, photo.id, { [editingField]: newValue || null });
      setPhoto(prev => ({ ...prev, [editingField]: newValue || null }));
      setEditingField(null);
      setDraftValue('');
    } catch (err) {
      setSaveError(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function toggleFavorite() {
    const next = !photo.Favorite;
    setSaving(true);
    setSaveError(null);
    try {
      const token = await getToken();
      await updatePhotoMetadata(token, photo.id, { Favorite: next });
      setPhoto(prev => ({ ...prev, Favorite: next }));
    } catch (err) {
      setSaveError(err.message);
    } finally {
      setSaving(false);
    }
  }

  const sharedFieldProps = { photo, editingField, draftValue, saving,
    onStart: startEdit, onCancel: cancelEdit, onChange: setDraftValue, onCommit: commitEdit };

  return (
    <div className="fv-lb-overlay" role="dialog" aria-modal="true">
      {/* Backdrop — click outside to close */}
      <div className="fv-lb-backdrop" onClick={onClose} />

      {/* Photo area */}
      <div className="fv-lb-photo-area"
        onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}
        onClick={e => e.stopPropagation()}
      >
        {prevPhoto && (
          <button className="fv-lb-nav fv-lb-nav--prev" onClick={onPrev} aria-label="Previous photo">
            &#8249;
          </button>
        )}

        <img
          className="fv-lb-img"
          src={photo.fullResUrl || photo.thumbnailUrl}
          alt={photo.Title || ''}
        />

        {nextPhoto && (
          <button className="fv-lb-nav fv-lb-nav--next" onClick={onNext} aria-label="Next photo">
            &#8250;
          </button>
        )}

        {/* Close button — floats top-right of photo area */}
        <button className="fv-lb-close" onClick={onClose} aria-label="Close lightbox">&#x2715;</button>
      </div>

      {/* Sidebar */}
      <aside className={`fv-lb-sidebar${sidebarOpen ? ' fv-lb-sidebar--open' : ''}`}
        onClick={e => e.stopPropagation()}>

        <div className="fv-lb-sidebar-header">
          <button className="fv-lb-sidebar-toggle" onClick={() => setSidebarOpen(v => !v)}>
            {sidebarOpen ? 'Hide info' : 'Info'}
          </button>
        </div>

        {sidebarOpen && (
          <div className="fv-lb-sidebar-body">
            {saveError && <p className="fv-lb-error">{saveError}</p>}

            <LbField label="Title" field="Title" {...sharedFieldProps} />
            <LbField label="Date" field="DateOfEvent" inputType="date" {...sharedFieldProps} />
            <LbField label="People" field="People" {...sharedFieldProps} />
            <LbField label="Project" field="Project" {...sharedFieldProps} />
            <LbField label="Tags" field="Tags" {...sharedFieldProps} />
            <LbField label="Location" field="Location" {...sharedFieldProps} />
            <LbField label="Caption" field="Caption" multiline {...sharedFieldProps} />
            <LbField label="Witness" field="Witness" multiline {...sharedFieldProps} />
            <LbField label="Doctrines" field="Doctrines" {...sharedFieldProps} />
            <LbField label="Source" field="Source" {...sharedFieldProps} />

            <div className="fv-lb-field">
              <span className="fv-lb-field-label">Tier</span>
              {editingField === 'Tier' ? (
                <select
                  className="fv-lb-field-input"
                  value={draftValue}
                  onChange={e => setDraftValue(e.target.value)}
                  onBlur={() => { if (!saving) commitEdit(); }}
                  onKeyDown={e => { if (e.key === 'Escape') cancelEdit(); }}
                  autoFocus
                >
                  <option value="">— unset —</option>
                  <option value="Archive">Archive</option>
                  <option value="Keeper">Keeper</option>
                  <option value="Treasure">Treasure</option>
                </select>
              ) : (
                <p
                  className={`fv-lb-field-value${!photo.Tier ? ' fv-lb-field-empty' : ''}`}
                  onClick={() => startEdit('Tier', photo.Tier ?? '')}
                >
                  {photo.Tier || '—'}
                </p>
              )}
              {editingField === 'Tier' && saving && <span className="fv-lb-saving">Saving…</span>}
            </div>

            <div className="fv-lb-field">
              <span className="fv-lb-field-label">Favorite</span>
              <button
                className={`fv-lb-fav-btn${photo.Favorite ? ' fv-lb-fav-btn--active' : ''}`}
                onClick={toggleFavorite}
                disabled={saving}
                aria-pressed={!!photo.Favorite}
              >
                {photo.Favorite ? '★ Favorited' : '☆ Favorite'}
              </button>
            </div>
          </div>
        )}
      </aside>
    </div>
  );
}
